import { useCallback } from "react";

import {
  convertToAssetId,
  trpc,
  useFolderById,
  useMoveAssetsToFolder,
  useSetPinnedCanvas,
} from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import { useEmitFolderDocumentationInteractionEvent } from "@zapier/shared";
import { AssetTypes, useNotification } from "@zapier/utils";

interface UsePinnedCanvasOptions {
  folderId: string;
  isFolderByIdEnabled: boolean;
  folderName?: string;
}

export function usePinnedCanvas({
  folderId,
  isFolderByIdEnabled,
  folderName,
}: UsePinnedCanvasOptions) {
  const t = useTranslations("folder-documentation");
  const { createNotification } = useNotification();
  const { emit } = useEmitFolderDocumentationInteractionEvent();

  const { folder: folderById, isLoading: isFolderByIdLoading } = useFolderById({
    folderId,
    enabled: isFolderByIdEnabled,
  });

  const { setPinnedCanvasAsync, isLoading: isPinning } = useSetPinnedCanvas({
    folderId,
  });

  const { moveAssetsToFolder, isPending: isMovingCanvas } =
    useMoveAssetsToFolder({
      folderId,
    });

  const { mutate: createCanvas, isPending: isCreatingCanvas } =
    trpc.canvas.create.useMutation();

  const pinCanvas = useCallback(
    async (canvasId: string) => {
      try {
        await setPinnedCanvasAsync({ folderId, pinnedCanvasId: canvasId });
        return true;
      } catch {
        createNotification({
          message: t("notifications.pinFailed"),
          type: "warning",
        });
        return false;
      }
    },
    [createNotification, folderId, setPinnedCanvasAsync, t],
  );

  const handlePinCanvas = useCallback(
    async (canvasId: string) => {
      const didPin = await pinCanvas(canvasId);
      if (didPin) {
        emit({
          optionSelected: "pin_existing_canvas",
          folderId,
          canvasId,
        });
      }
    },
    [emit, folderId, pinCanvas],
  );

  const handleUnpinCanvas = useCallback(async () => {
    emit({ optionSelected: "unpin_canvas", folderId });
    try {
      // Backend expects explicit null to unpin.
      // eslint-disable-next-line unicorn/no-null
      await setPinnedCanvasAsync({ folderId, pinnedCanvasId: null });
    } catch {
      createNotification({
        message: t("notifications.unpinFailed"),
        type: "warning",
      });
    }
  }, [createNotification, emit, folderId, setPinnedCanvasAsync, t]);

  const handleCreateCanvas = useCallback(() => {
    emit({ optionSelected: "create_new_canvas", folderId });
    createCanvas(
      { title: folderName || "" },
      {
        onSuccess: (canvas) => {
          const canvasAssetId = convertToAssetId(AssetTypes.Canvas, canvas.id);
          moveAssetsToFolder(
            {
              assetIds: [canvasAssetId],
              destinationFolderId: folderId,
            },
            {
              onSuccess: () => {
                void (async () => {
                  const didPin = await pinCanvas(canvas.id);
                  if (didPin) {
                    emit({
                      optionSelected: "pin_new_canvas",
                      folderId,
                      canvasId: canvas.id,
                    });
                  }
                })();
              },
              onError: () => {
                createNotification({
                  message: t("notifications.addToFolderFailed"),
                  type: "warning",
                });
              },
            },
          );
        },
        onError: () => {
          createNotification({
            message: t("notifications.createFailed"),
            type: "warning",
          });
        },
      },
    );
  }, [
    createCanvas,
    createNotification,
    emit,
    folderId,
    folderName,
    moveAssetsToFolder,
    pinCanvas,
    t,
  ]);

  return {
    pinnedCanvasId: folderById?.pinnedCanvasId,
    isPinnedCanvasIdLoading: isFolderByIdEnabled && isFolderByIdLoading,
    isPinning,
    isCreating: isCreatingCanvas || isMovingCanvas,
    handlePinCanvas,
    handleUnpinCanvas,
    handleCreateCanvas,
  };
}
